import { Typography, Stack, Box } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import { EnumProjectMilestoneStatus } from 'tdc-web-backend/enums/enums';

import { useTranslation } from 'react-i18next';
import { calculateProjectProgress, formatDateFns } from '../../../utils/helpers';
import ProgressBar from '../../../components/progress-bar/ProgressBar';
import StatusChipField from '../../../components/StatusChipField/StatusChipField';
import CustomTable from '../../../components/custom-table/CustomTable';
import CustomButton, { Variants } from '../../../components/button/CustomButton';
import { ReactComponent as PlusIcon } from '../../../assets/icons/button-icons/PlusIcon.svg';
import useAuth from '../../../utils/hooks/useAuth';
import CustomTooltip from '../../../components/tooltip/CustomTooltip';
import { EnumMode } from '../../../utils/context/Mode.context';
import useMode from '../../../utils/hooks/useMode';

export interface ProjectListTableProps {
  dataGridHeight?: string | number;
}

const ProjectListTable = ({ dataGridHeight = '300px' }: ProjectListTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    authData: { userData },
  } = useAuth();

  const { mode } = useMode();

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('common.status'),
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => <StatusChipField text={params.value} />,
    },
    {
      field: 'name',
      headerName: t('common.name'),
      flex: 0.3,
      renderCell: (params: GridRenderCellParams) => (
        <CustomTooltip title={params.value} placement="top">
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: 'primaryDark.600',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            {params.value}
          </Typography>
        </CustomTooltip>
      ),
    },
    {
      field: 'start',
      headerName: t('common.startDate'),
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value !== null) {
          return <Typography>{formatDateFns(params.value)}</Typography>;
        }

        return <Typography>-</Typography>;
      },
    },
    {
      field: 'end',
      headerName: t('common.endDate'),
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value !== null) {
          return <Typography>{formatDateFns(params.value)}</Typography>;
        }

        return <Typography>-</Typography>;
      },
    },
    {
      field: 'milestoneCompletedCount',
      headerName: t('common.progress'),
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => {
        const {
          row: {
            tasksCount,
            milestoneCount,
            tasksCompletedCount,
            tasksCanceledCount,
            milestoneCompletedCount,
            milestoneApprovedCount,
          },
        } = params;
        const percentageOfCompletion = calculateProjectProgress(
          tasksCount,
          milestoneCount,
          tasksCompletedCount,
          tasksCanceledCount,
          milestoneCompletedCount,
          milestoneApprovedCount,
        );

        return (
          <ProgressBar
            value={Number.isFinite(percentageOfCompletion) ? percentageOfCompletion : 0}
          />
        );
      },
    },
  ];

  const panelConfig = [
    {
      id: 'project_list-all',
      title: t('filters.all'),
    },
    {
      id: 'project_list-in_progress',
      title: t('filters.inProgress'),
      search: { status: EnumProjectMilestoneStatus.InProgress },
    },
    {
      id: 'project_list-completed',
      title: t('filters.completed'),
      search: { status: EnumProjectMilestoneStatus.Completed },
    },
    {
      id: 'project_list-pending',
      title: t('filters.pending'),
      search: { status: EnumProjectMilestoneStatus.Pending },
    },
  ];

  return (
    <Stack direction="column" sx={{ height: 'fit-content', width: '100%' }} spacing={2}>
      <Box sx={{ position: 'relative' }}>
        <CustomButton
          variant="primaryLight"
          sx={{
            height: { md: '3.75rem', lg: '2.75rem' },
            width: { md: '8.5rem', lg: '10.5rem', gr: '13.5rem' },
            position: 'absolute',
            right: 0,
            top: { md: 0, lg: 10 },
            cursor: 'pointer',
            zIndex: 1,
          }}
          startIcon={<PlusIcon width="20px" height="20px" />}
          onClick={() => navigate('./project-request')}
        >
          <Typography variant="bodyMedium3">{t('projects.requestNewProject')}</Typography>
        </CustomButton>
        <CustomTable
          resource="projects"
          columns={columns}
          tabsClassName="react-joyride-step-9"
          className="react-joyride-step-10"
          panelConfig={panelConfig}
          searchParams={{
            ...(mode === EnumMode.Client && { buyer: userData?.membership?.company }),
            ...(mode === EnumMode.Supplier && { seller: userData?.membership?.company }),
          }}
          onRowClick={(params) => navigate(`./project/${params.id}`)}
          height={dataGridHeight as string}
          hideFooter
        />
      </Box>
    </Stack>
  );
};

export default ProjectListTable;
